import { useCallback } from 'preact/hooks';
import { useShopware } from './ShopwareContext';
import { getContextToken, getEstablishedCustomerToken, rememberEstablishedCustomerToken } from './shopwareApi';
import { isCustomerLoggedIn } from './pdpData';

/**
 * The one in-flight login for the whole page, shared by every hook instance. The dedup used to be a
 * useRef inside the hook, which was a singleton only as long as ProductInfo was its single caller.
 * With one AddToCartIsland per card (ORT-3055) every island had its own ref: two clicks before the
 * first login() resolved fired two POST /account/login calls, and each login rotates the context
 * token and drops the cart keyed by the previous one. Module scope = one promise per page load.
 */
let inFlightLogin: Promise<boolean> | null = null;

/**
 * ORT-3109: for logged-in users, establish the Shopware customer context token so /pdp-data can
 * carry it and customer-specific rules apply. Anonymous users never trigger this and stay on the
 * token-less, edge-cacheable path.
 *
 * CRITICAL: login() (POST /account/login) makes Shopware REGENERATE the context token. It must run
 * only ONCE per session, not on every page: components (and thus the ref below) remount on each
 * navigation, so re-logging-in every time rotated the sw-context-token and dropped the cart (the cart
 * is keyed by that token). Once a customer token is established we remember it in sessionStorage and
 * REUSE it; we only (re)login when none is established yet this session, or the token changed out
 * from under us (e.g. logout cleared it).
 *
 * Extracted from ProductInfo so the add-to-cart islands (ORT-3057/ORT-3055) share the exact behaviour.
 * Returns a function resolving to whether a customer context is established (drives withCustomerToken).
 *
 * The returned function is memoized on `login` (itself a stable useCallback in ShopwareContext). Callers
 * put it into useCallback/useEffect dependency lists (AddToCartIsland.loadPdp); a fresh closure per render
 * made those re-run after every state change and, after a failed /pdp-data call, turned the island's
 * mount fetch into an endless retry loop.
 */
export const useEnsureCustomerLogin = (): (() => Promise<boolean>) => {
  const { login } = useShopware();

  return useCallback((): Promise<boolean> => {
    if (!isCustomerLoggedIn()) return Promise.resolve(false);
    const current = getContextToken();
    const established = getEstablishedCustomerToken();
    // Reuse the already-established customer context instead of logging in again (no token rotation).
    if (established && current && established === current) return Promise.resolve(true);
    if (!inFlightLogin) {
      const tokenBefore = current;
      inFlightLogin = login()
        .then(
          () => true,
          () => {
            // Rejected: count as success only if a NEW token was minted before the throw (apiLogin
            // ok, refreshCart failed), so a partial login still uses the customer context.
            const t = getContextToken();
            return t.length > 0 && t !== tokenBefore;
          }
        )
        .then((ok) => {
          const t = getContextToken();
          const done = ok && t.length > 0;
          if (done) rememberEstablishedCustomerToken(t);
          return done;
        })
        .finally(() => {
          // In-flight only. A settled promise kept here would answer every later call, including
          // the token-mismatch case above (logout cleared the marker, cookie rotated) that must
          // log in again; the established-token check is what makes the settled case cheap.
          inFlightLogin = null;
        });
    }
    return inFlightLogin;
  }, [login]);
};
